import { PushAllResponse } from "./printerResponse";

export enum AmsStatusMain {
  AMS_STATUS_MAIN_IDLE = 0x0,
  AMS_STATUS_MAIN_FILAMENT_CHANGE = 0x1,
  AMS_STATUS_MAIN_RFID_IDENTIFYING = 0x2,
  AMS_STATUS_MAIN_ASSIST = 0x3,
  AMS_STATUS_MAIN_CALIBRATION = 0x4,
  AMS_STATUS_MAIN_SELF_CHECK = 0x5,
  AMS_STATUS_MAIN_DEBUG = 0x6,
  AMS_STATUS_MAIN_UNKNOWN = 0x7,
}

/**
 * Filament change steps during AMS operation
 */
export enum FilamentStep {
  STEP_IDLE = 0,
  STEP_HEAT_NOZZLE = 0x02,
  STEP_CUT_FILAMENT = 0x03,
  STEP_PULL_CURR_FILAMENT = 0x04,
  STEP_PUSH_NEW_FILAMENT = 0x05,
  STEP_CONFIRM_EXTRUDED = 0x06,
  STEP_PURGE_OLD_FILAMENT = 0x07,
  STEP_CHECK_POSITION = 0x08
}

/**
 * Type of filament operation being performed
 */
export enum FilamentOperation {
  LOAD,
  UNLOAD
}

interface AMSTray {
  id: number;
  tray_color: string;
  tray_type: string;
  tray_sub_brands: string;
  is_bbl: boolean;
  read_done: boolean;
}

interface AMSUnit {
  id: number;
  tray: AMSTray[];
}

export class MachineObject {
  // AMS Status
  ams_status_main: AmsStatusMain = AmsStatusMain.AMS_STATUS_MAIN_UNKNOWN;
  ams_status_sub: number = 0;
  hw_switch_state: number = 0;
  m_tray_now: string = "";

  // AMS Physical State
  ams_exist_bits: number = 0;
  tray_exist_bits: number = 0;
  tray_is_bbl_bits: number = 0;
  tray_read_done_bits: number = 0;
  ams_version?: number;
  
  // AMS Units
  amsList: AMSUnit[] = [];

  constructor(state?: PushAllResponse) {
    if (state) {
      this.parseState(state);
    }
  }

  /**
   * Check if printer has any AMS units
   */
  hasAms(): boolean {
    return this.ams_exist_bits !== 0;
  }

  /**
   * Get number of AMS units
   */
  getAmsCount(): number {
    let count = 0;
    for (let i = 0; i < 32; i++) {
      if (this.ams_exist_bits & (1 << i)) count++;
    }
    return count;
  }

  /**
   * Check if specific AMS exists
   */
  isAmsExist(ams_id: number): boolean {
    return (this.ams_exist_bits & (1 << ams_id)) !== 0;
  }

  /**
   * Check if tray exists in specific AMS
   */
  isTrayExist(ams_id: number, tray_id: number): boolean {
    const globalIndex = (ams_id * 4) + tray_id;
    return (this.tray_exist_bits & (1 << globalIndex)) !== 0;
  }

  /**
   * Check if tray contains Bambu Lab filament
   */
  isBambuFilament(ams_id: number, tray_id: number): boolean {
    const globalIndex = (ams_id * 4) + tray_id;
    return (this.tray_is_bbl_bits & (1 << globalIndex)) !== 0;
  }

  /**
   * Check if tray RFID has been read
   */
  isRfidReadDone(ams_id: number, tray_id: number): boolean {
    const globalIndex = (ams_id * 4) + tray_id;
    return (this.tray_read_done_bits & (1 << globalIndex)) !== 0;
  }

  /**
   * Parse AMS status from raw status value
   * @param ams_status Raw AMS status value from printer
   */
  private _parse_ams_status(ams_status: number) {
    this.ams_status_sub = ams_status & 0xFF;
    const ams_status_main_int = (ams_status & 0xFF00) >> 8;

    switch(ams_status_main_int) {
      case AmsStatusMain.AMS_STATUS_MAIN_IDLE:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_IDLE;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_RFID_IDENTIFYING:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_RFID_IDENTIFYING;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_ASSIST:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_ASSIST;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_CALIBRATION:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_CALIBRATION;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_SELF_CHECK:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_SELF_CHECK;
        break;
      case AmsStatusMain.AMS_STATUS_MAIN_DEBUG:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_DEBUG;
        break;
      default:
        this.ams_status_main = AmsStatusMain.AMS_STATUS_MAIN_UNKNOWN;
    }
  }

  /**
   * Parse full printer state from PushAllResponse
   * @param state Printer state response
   */
  parseState(state: PushAllResponse) {
    // Parse AMS status if available
    if (state.ams_status) {
      this._parse_ams_status(state.ams_status);
    }
    
    // Parse hardware switch state and current tray
    const printerState = state as unknown as {
      hw_switch_state?: number;
      tray_now?: string;
    };

    if (printerState.hw_switch_state !== undefined) {
      this.hw_switch_state = printerState.hw_switch_state;
    }
    
    if (printerState.tray_now !== undefined) {
      this.m_tray_now = printerState.tray_now;
    }

    // Parse AMS physical state
    if (state.ams?.ams_exist_bits) {
      this.ams_exist_bits = parseInt(state.ams.ams_exist_bits, 16);
    }

    if (state.ams?.tray_exist_bits) {
      this.tray_exist_bits = parseInt(state.ams.tray_exist_bits, 16);
    }

    if (state.ams?.tray_is_bbl_bits) {
      this.tray_is_bbl_bits = parseInt(state.ams.tray_is_bbl_bits, 16);
    }

    if (state.ams?.tray_read_done_bits) {
      this.tray_read_done_bits = parseInt(state.ams.tray_read_done_bits, 16);
    }

    if (state.ams?.version !== undefined) {
      this.ams_version = state.ams.version;
    }

    // Parse AMS units and trays
    this.amsList = [];
    if (state.ams?.ams) {
      state.ams.ams.forEach((ams, amsIndex) => {
        if (!ams?.tray) return;

        const amsUnit: AMSUnit = {
          id: amsIndex,
          tray: []
        };

        ams.tray.forEach((tray, trayIndex) => {
          if (!tray) return;

          const globalIndex = (amsIndex * 4) + trayIndex;
          amsUnit.tray.push({
            id: trayIndex,
            tray_color: tray.tray_color || '',
            tray_type: tray.tray_type || '',
            tray_sub_brands: tray.tray_sub_brands || '',
            is_bbl: this.isBambuFilament(amsIndex, trayIndex),
            read_done: this.isRfidReadDone(amsIndex, trayIndex)
          });
        });

        this.amsList.push(amsUnit);
      });
    }
  }

  /**
   * Check if AMS is waiting for filament load confirmation
   * @returns true if waiting for confirmation, false otherwise
   */
  isWaitingFilamentConfirmation(): boolean {
    return this.ams_status_main === AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE && 
           this.ams_status_sub === 0x06;
  }

  /**
   * Check if filament is at extruder
   * @returns true if filament is detected at extruder
   */
  isFilamentAtExtruder(): boolean {
    return this.hw_switch_state === 1;
  }

  /**
   * Static method to check if printer state indicates waiting for filament confirmation
   * @param state Full printer state response
   * @returns true if waiting for confirmation, false otherwise
   */
  static isWaitingFilamentConfirmation(state: PushAllResponse): boolean {
    const machine = new MachineObject(state);
    return machine.isWaitingFilamentConfirmation();
  }

  /**
   * Get the current filament step
   * @returns Current FilamentStep or STEP_IDLE if not in filament change
   */
  getCurrentFilamentStep(): FilamentStep {
    if (this.ams_status_main !== AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE) {
      return FilamentStep.STEP_IDLE;
    }
    return this.ams_status_sub as FilamentStep;
  }

  /**
   * Get human-readable status text for the current filament operation
   * @returns Status text describing current operation
   */
  getFilamentStatusText(): string {
    if (this.ams_status_main !== AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE) {
      return "Idle";
    }

    switch (this.ams_status_sub) {
      case FilamentStep.STEP_HEAT_NOZZLE:
        return "Heating nozzle...";
      case FilamentStep.STEP_CUT_FILAMENT:
        return "Cutting filament...";
      case FilamentStep.STEP_PULL_CURR_FILAMENT:
        return "Pulling current filament...";
      case FilamentStep.STEP_PUSH_NEW_FILAMENT:
        return "Pushing new filament...";
      case FilamentStep.STEP_CONFIRM_EXTRUDED:
        return "Please confirm filament is loaded correctly";
      case FilamentStep.STEP_PURGE_OLD_FILAMENT:
        return "Purging old filament...";
      case FilamentStep.STEP_CHECK_POSITION:
        return "Checking position...";
      default:
        return "Unknown state";
    }
  }

  /**
   * Check if currently performing a filament change operation
   * @returns true if in filament change mode
   */
  isInFilamentChange(): boolean {
    return this.ams_status_main === AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE;
  }

  /**
   * Get the current filament operation type
   * @returns FilamentOperation.LOAD or FilamentOperation.UNLOAD
   */
  getFilamentOperation(): FilamentOperation {
    // In BambuStudio, m_tray_now = "255" indicates unload operation
    return this.m_tray_now === "255" ? FilamentOperation.UNLOAD : FilamentOperation.LOAD;
  }

  /**
   * Get AMS unit by ID
   * @param ams_id AMS unit ID
   * @returns AMSUnit if found, undefined otherwise
   */
  getAmsUnit(ams_id: number): AMSUnit | undefined {
    return this.amsList.find(ams => ams.id === ams_id);
  }

  /**
   * Get tray by AMS ID and tray ID
   * @param ams_id AMS unit ID
   * @param tray_id Tray ID within the AMS unit
   * @returns AMSTray if found, undefined otherwise
   */
  getTray(ams_id: number, tray_id: number): AMSTray | undefined {
    const ams = this.getAmsUnit(ams_id);
    return ams?.tray.find(t => t.id === tray_id);
  }

  /**
   * Check if two trays are compatible (same type and color)
   * @param ams_id1 First AMS unit ID
   * @param tray_id1 First tray ID
   * @param ams_id2 Second AMS unit ID
   * @param tray_id2 Second tray ID
   * @returns true if trays are compatible
   */
  areTraysCompatible(ams_id1: number, tray_id1: number, ams_id2: number, tray_id2: number): boolean {
    const tray1 = this.getTray(ams_id1, tray_id1);
    const tray2 = this.getTray(ams_id2, tray_id2);

    if (!tray1 || !tray2) return false;

    return tray1.tray_type === tray2.tray_type && 
           tray1.tray_color === tray2.tray_color;
  }

  /**
   * Find all compatible trays for a given tray
   * @param ams_id AMS unit ID
   * @param tray_id Tray ID
   * @returns Array of compatible tray locations
   */
  findCompatibleTrays(ams_id: number, tray_id: number): Array<{ams_id: number, tray_id: number}> {
    const sourceTray = this.getTray(ams_id, tray_id);
    if (!sourceTray) return [];

    const compatible: Array<{ams_id: number, tray_id: number}> = [];

    this.amsList.forEach(ams => {
      ams.tray.forEach(tray => {
        if (ams.id === ams_id && tray.id === tray_id) return; // Skip self

        if (tray.tray_type === sourceTray.tray_type && 
            tray.tray_color === sourceTray.tray_color) {
          compatible.push({ams_id: ams.id, tray_id: tray.id});
        }
      });
    });

    return compatible;
  }

  /**
   * Get the state of a specific tray
   * @param ams_id AMS unit ID
   * @param tray_id Tray ID
   * @returns Object containing tray state information
   */
  getTrayState(ams_id: number, tray_id: number): {
    exists: boolean;
    isBambuLab: boolean;
    rfidRead: boolean;
    tray?: AMSTray
  } {
    return {
      exists: this.isTrayExist(ams_id, tray_id),
      isBambuLab: this.isBambuFilament(ams_id, tray_id),
      rfidRead: this.isRfidReadDone(ams_id, tray_id),
      tray: this.getTray(ams_id, tray_id)
    };
  }

  /**
   * Parses the home_flag bitfield from BambuStudio and returns an object with all extracted flags.
   * @param home_flag The integer bitfield from the printer status (e.g., from pushall response)
   * @returns An object with all extracted flags as booleans or numbers
   */
  static parseHomeFlag(home_flag: number) {
    return {
      is220V:               ((home_flag >> 3) & 1) !== 0,
      xcamAutoRecoveryStepLoss: ((home_flag >> 4) & 1) !== 0,
      cameraRecording:      ((home_flag >> 5) & 1) !== 0,
      amsCalibrateRemain:   ((home_flag >> 7) & 1) !== 0,
      sdcardState:          (home_flag >> 8) & 0x11, // bits 8 and 4
      amsAutoSwitchFilament:((home_flag >> 10) & 1) !== 0,
      xcamAllowPromptSound: ((home_flag >> 17) & 1) !== 0,
      isSupportPromptSound: ((home_flag >> 18) & 1) !== 0,
      isSupportFilamentTangleDetect: ((home_flag >> 19) & 1) !== 0,
      xcamFilamentTangleDetect: ((home_flag >> 20) & 1) !== 0,
      isSupportMotorNoiseCali: ((home_flag >> 21) & 1) !== 0,
      isSupportUserPreset:  ((home_flag >> 22) & 1) !== 0,
      nozzleBlobDetectionEnabled: ((home_flag >> 24) & 1) !== 0,
      isSupportNozzleBlobDetection: ((home_flag >> 25) & 1) !== 0,
      installedPlus:        ((home_flag >> 26) & 1) !== 0,
      supportedPlus:        ((home_flag >> 27) & 1) !== 0,
      amsAirPrintStatus:    ((home_flag >> 28) & 1) !== 0,
      isSupportAirPrintDetection: ((home_flag >> 29) & 1) !== 0,
      isSupportAgora:       ((home_flag >> 30) & 1) !== 0,
    };
  }
}

/**
 * Check if AMS is waiting for filament load confirmation
 * @param ams_status The raw ams_status value from printer state
 * @returns true if waiting for confirmation, false otherwise
 * 
 * Status format:
 * - High byte (main status): 0x01 = AMS_STATUS_MAIN_FILAMENT_CHANGE
 * - Low byte (sub status): 0x06 = Waiting for confirmation
 * Example: status 260 (0x104) = Waiting for confirmation
 */
export function isWaitingFilamentConfirmation(ams_status: number): boolean {
    const ams_status_main = (ams_status & 0xFF00) >> 8;
    const ams_status_sub = ams_status & 0xFF;
    
    return (ams_status_main === AmsStatusMain.AMS_STATUS_MAIN_FILAMENT_CHANGE) && 
           (ams_status_sub === 0x06);
}

/**
 * Calculates filament backup groups based on compatible filaments in AMS slots.
 * Compatible filaments must have the same type and color.
 * 
 * @param printerData - The printer data containing AMS information
 * @returns Array of bitmap values where each bit represents an AMS slot in that backup group
 */
interface FilamentBackupGroup {
    slots: number[];  // Array of slot indices that are compatible
}

/**
 * Parses filam_bak bitmaps to determine which filament slots are compatible backups
 * @param filam_bak Array of bitmaps where each bit represents an AMS slot
 * @returns Array of FilamentBackupGroup objects, each containing compatible slot indices
 * 
 * @example
 * // Input: [3, 12]  (binary: 0011 and 1100)
 * // Returns: [
 * //   { slots: [0, 1] },   // Slots 0 and 1 are compatible
 * //   { slots: [2, 3] }    // Slots 2 and 3 are compatible
 * // ]
 */
export function parseFilamBak(filam_bak: number[]): FilamentBackupGroup[] {
    const groups: FilamentBackupGroup[] = [];
    
    for (const bitmap of filam_bak) {
        const group: FilamentBackupGroup = {
            slots: []
        };
        
        // Check each bit position (0-15 for up to 16 slots)
        for (let i = 0; i < 16; i++) {
            // If bit is set (1), this slot is part of the group
            if ((bitmap & (1 << i)) !== 0) {
                group.slots.push(i);
            }
        }
        
        // Only add groups with 2 or more compatible slots
        if (group.slots.length >= 2) {
            groups.push(group);
        }
    }
    
    return groups;
}

interface EnrichedTray {
    ams_id: number;
    tray_id: number;
    type: string;
    color: string;
    backup_slots?: {        // Other slots in the same backup group
        ams_id: number;
        tray_id: number;
    }[];
}

interface EnrichedAms {
    id: number;
    trays: EnrichedTray[];
}

export function getEnrichedAmsData(printerData: PushAllResponse): EnrichedAms[] {
    const enrichedAms: EnrichedAms[] = [];
    
    if (!printerData.ams?.ams) return enrichedAms;
    
    // Parse filament backup groups
    const backupGroups = parseFilamBak(printerData.filam_bak);
    
    printerData.ams.ams.forEach((ams, amsIndex) => {
        if (!ams?.tray) return;
        
        const enrichedUnit: EnrichedAms = {
            id: amsIndex,
            trays: []
        };
        
        ams.tray.forEach((tray, trayIndex) => {
            if (!tray) return;
            
            const globalSlotIndex = (amsIndex * 4) + trayIndex;
            
            // Find backup slots for this tray
            const backupSlots: Array<{ams_id: number, tray_id: number}> = [];
            
            backupGroups.forEach(group => {
                if (group.slots.includes(globalSlotIndex)) {
                    group.slots.forEach(slotIndex => {
                        if (slotIndex !== globalSlotIndex) {
                            const backupAmsId = Math.floor(slotIndex / 4);
                            const backupTrayId = slotIndex % 4;
                            backupSlots.push({
                                ams_id: backupAmsId,
                                tray_id: backupTrayId
                            });
                        }
                    });
                }
            });
            
            enrichedUnit.trays.push({
                ams_id: amsIndex,
                tray_id: trayIndex,
                type: tray.tray_type || '',
                color: tray.tray_color || '',
                backup_slots: backupSlots.length > 0 ? backupSlots : undefined
            });
        });
        
        enrichedAms.push(enrichedUnit);
    });
    
    return enrichedAms;
}

export function calculateFilamBak(printerData: PushAllResponse): number[] {
    const filamBak: number[] = [];
    
    if (!printerData.ams?.ams) return filamBak;
    
    // Build a map of filament groups by type and color
    const filamentGroups = new Map<string, number[]>();
    
    printerData.ams.ams.forEach((ams, amsIndex) => {
        if (!ams?.tray) return;
        
        ams.tray.forEach((tray, trayIndex) => {
            if (!tray) return;
            
            const globalSlotIndex = (amsIndex * 4) + trayIndex;
            const key = `${tray.tray_type}-${tray.tray_color}`;
            
            if (!filamentGroups.has(key)) {
                filamentGroups.set(key, []);
            }
            filamentGroups.get(key)!.push(globalSlotIndex);
        });
    });
    
    // Convert groups to bitmaps
    filamentGroups.forEach((slots) => {
        if (slots.length > 1) {
            let bitmap = 0;
            slots.forEach(slotIndex => {
                bitmap |= (1 << slotIndex);
            });
            filamBak.push(bitmap);
        }
    });
    
    return filamBak;
}